// PLAN.md §2.4 — HUD with logo + MoneyPill / MoneyMenu.

import { useEffect, useRef, useState } from 'react'
import { motion } from 'motion/react'
import { AnimateNumber } from 'motion-plus/react'
import { useStore } from '../store.js'
import { fmtUsdAbbrev } from '../format.js'
import { DeltaNumber } from './ported/index.js'

export function TopBar() {
  const self = useStore((s) => s.self)
  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const [open, setOpen] = useState(false)

  const portfolioVersion = self?.portfolioVersion ?? 0
  const lastVersion = useRef(portfolioVersion)

  useEffect(() => {
    if (portfolioVersion === lastVersion.current) return
    lastVersion.current = portfolioVersion
    const el = buttonRef.current
    if (!el) return
    el.classList.remove('animate-pulse-bump')
    void el.offsetWidth
    el.classList.add('animate-pulse-bump')
  }, [portfolioVersion])

  if (!self) return null

  const total = self.bankroll + self.active_stake
  const useAbbrev = total >= 10_000_000
  const pnl = total - self.starting_capital
  const pnlPct = self.starting_capital > 0 ? (pnl / self.starting_capital) * 100 : 0

  return (
    <header className="flex items-center justify-between px-6 h-14 border-b border-light-grey">
      <span className="text-logo text-black">TRADING GROUNDS</span>
      <div className="relative">
        <motion.button
          ref={buttonRef}
          type="button"
          onClick={() => setOpen((o) => !o)}
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          transition={{ type: 'spring', stiffness: 400, damping: 25 }}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label="Total balance"
          className="h-11 bg-light-grey rounded-full px-5 flex items-center justify-center"
        >
          <span className="text-body text-black tabular-nums inline-block">
            {useAbbrev ? (
              fmtUsdAbbrev(total)
            ) : (
              <AnimateNumber
                prefix="$"
                locales="en-US"
                format={{ minimumFractionDigits: 2, maximumFractionDigits: 2 }}
              >
                {total}
              </AnimateNumber>
            )}
          </span>
        </motion.button>
        {open && (
          <MoneyMenuPanel
            total={total}
            startingCapital={self.starting_capital}
            pnl={pnl}
            pnlPct={pnlPct}
            onClose={() => setOpen(false)}
          />
        )}
      </div>
    </header>
  )
}

function MoneyMenuPanel({
  total,
  startingCapital,
  pnl,
  pnlPct,
  onClose,
}: {
  total: number
  startingCapital: number
  pnl: number
  pnlPct: number
  onClose: () => void
}) {
  useEffect(() => {
    function onPointer(e: MouseEvent) {
      const t = e.target as Node
      if ((t as Element)?.closest?.('[data-menu="money"]')) return
      onClose()
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [onClose])

  return (
    <motion.div
      role="menu"
      data-menu="money"
      initial={{ opacity: 0, scale: 0.96, y: -4 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
      className="absolute right-0 mt-2 min-w-[240px] bg-white rounded-xl p-1.5 border border-light-grey shadow-big z-50"
    >
      <Row label="Total" value={total} />
      <Row label="Starting capital" value={startingCapital} />
      <div className="flex items-center justify-between px-3 py-2 gap-8">
        <span className="text-body text-dark-grey">PnL</span>
        <DeltaNumber value={pnl} format="usd" compoundPercent={pnlPct} />
      </div>
    </motion.div>
  )
}

function Row({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex items-center justify-between px-3 py-2 gap-8">
      <span className="text-body text-dark-grey">{label}</span>
      <span className="text-body text-black tabular-nums">
        <AnimateNumber
          prefix="$"
          locales="en-US"
          format={{ minimumFractionDigits: 2, maximumFractionDigits: 2 }}
        >
          {value}
        </AnimateNumber>
      </span>
    </div>
  )
}
